<template>
  <el-form
    ref="formRef"
    class="form-rule"
    :model="state.localDataForm"
    label-position="right"
    :label-width="props.labelWidth"
    :disabled="disabled"
    v-bind="$attrs"
    @submit.prevent=""
  >
    <template
      v-for="itemConfig in props.formConfigs?.filter(c => c.hidden !== true)"
      :key="itemConfig.prop"
    >
      <slot v-if="itemConfig.slot" :name="itemConfig.slot" />

      <el-form-item
        v-else
        class="form-item"
        :class="{
          'form-fit':
            itemConfig.fit == true || (props.fit && itemConfig.fit == undefined)
        }"
        :style="{ width: itemConfig.width, ...itemConfig?.style }"
        :prop="itemConfig.prop"
        :label="t(itemConfig.label)"
        :rules="itemConfig.rules || basicRules(itemConfig)"
        :label-width="
          getLabelWidth(
            String(itemConfig.labelWidth || props.labelWidth),
            itemConfig.tipContent
          )
        "
      >
        <div style="display: inline-flex; width: 100%">
          <el-tooltip
            v-if="itemConfig.tipContent"
            style="width: 10px !important; height: 10px !important"
            effect="dark"
            placement="top-start"
            :content="t(itemConfig.tipContent)"
          >
            <div
              style="
                display: inline-flex;
                align-self: center;
                margin-right: 6px;
              "
            />
          </el-tooltip>
          <!-- 4.2 通常展示的信息 -->
          <component
            :is="getComponent(itemConfig)"
            v-model="state.localDataForm[itemConfig.prop]"
            v-model:fileUrl="state.localDataForm[itemConfig.prop]"
            :disabled="disabled"
            v-bind="{ ...itemConfig }"
            :config="itemConfig"
            :formRef="formRef"
          />
        </div>
      </el-form-item>
    </template>
  </el-form>
</template>
<script setup lang="ts">
import { ref, reactive, watchEffect, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { ElMessage } from "element-plus";

const props = defineProps({
  formConfigs: {
    type: Array as () => FormItemConfig[],
    required: true
  },
  dataForm: {
    type: Object as () => Record<string, any>,
    required: true
  },
  isRequired: {
    type: Boolean,
    default: true
  },
  labelWidth: {
    type: String,
    default: "100px"
  },
  disabled: {
    type: Boolean,
    default: false
  },
  fit: {
    //行内表单
    type: Boolean,
    default: false
  }
});

const emit = defineEmits<{
  (event: "update:dataForm", value: Record<string, any>): void;
}>();

const { t } = useI18n();
const formRef = ref<any>(null);

const state = reactive({
  localDataForm: props.dataForm,
  languageList: []
});

// Sync dataForm prop to state.localDataForm
watchEffect(() => {
  state.localDataForm = props.dataForm;
});

const getComponent = (value: FormItemConfig) => {
  const type = value.type;
  switch (type) {
    case "text":
    case "textarea":
    case "password":
      return "form-input";
    case "select":
      return "form-select";
    case "datetime":
    case "datetimerange":
    case "daterange":
    case "date":
    case "year":
      return "form-date";
    case "number":
      return "form-number";
    case "radio":
      return "form-radio";
    case "switch":
      return "form-switch";
    case "checkbox":
      return "form-checkbox";
    default:
      if (value.componentName) {
        return value.componentName;
      }
      ElMessage.info("组件类型不存在");
      return "";
  }
};

const basicRules = (itemConfig: FormItemConfig) => {
  return [
    {
      required:
        itemConfig.isRequired === undefined
          ? props.isRequired
          : itemConfig.isRequired,
      message: t("validate.required"),
      trigger:
        itemConfig.type === "radio" || itemConfig.type === "select"
          ? "change"
          : ["blur", "change"]
    }
  ];
};

const validate = async (): Promise<boolean> => {
  try {
    await formRef.value.validate();
    return true;
  } catch (e) {
    return false;
  }
};

const reset = () => {
  formRef.value.resetFields();
};
const resetFields = () => {
  formRef.value.resetFields();
};

const clearValidate = (arr: string[]) => {
  formRef.value.clearValidate(arr);
};
defineExpose({
  validate,
  reset,
  clearValidate,
  resetFields
});
const getLabelWidth = (baseLabelWidth: string, tipContent: string) => {
  baseLabelWidth = (parseInt(baseLabelWidth) || props.labelWidth)?.toString();
  return tipContent ? parseInt(baseLabelWidth) - 20 : parseInt(baseLabelWidth);
};
</script>
<style lang="scss" scoped>
.form-item {
  display: inline-flex;
  width: 100%;
  margin-bottom: 24px;

  :deep(.el-select) {
    width: 100%;
  }

  :deep(.el-input-group__append) {
    width: 25%;

    .el-select {
      width: unset;
    }
  }
}

.form-item.form-fit {
  display: inline-flex;
  width: 420px;
  margin-right: 40px;
}

html:not(.dark).form-gap {
  position: relative;
  width: calc(100% + 100px);
  height: 16px;
  margin-left: -50px;
  background-color: hsl(228deg 29% 97%);
}

.el-form .tab-title {
  margin-top: 16px;
  margin-bottom: 32px;
}
</style>
